/**
 * `sync.studyGroups.group(id).decks.*` — top-level deck operations within
 * a group: list, share-new (`add`), and move-between-folders. The
 * per-deck facade returned by `.deck(deckId)` lives in `scoped-deck.ts`.
 */

import type { SGDeckProps } from '@flashka/shared/sync-engine/schema/group-entities';
import type { SyncCardProps } from '@flashka/shared/sync-engine/schema/shared-types';

import { useShallow } from 'zustand/react/shallow';

import { useStudyGroupStore } from '../internal/store/study-group-store';
import { uuid } from '../internal/uuid';
import {
  currentUserId,
  nowISO,
  sendSGWithPreImage,
  stripCardForSG,
  updateDeckProperty,
} from './helpers';
import { sgScopedDeck } from './scoped-deck';
import type { SGScopedGroup } from './types';

export function createDecksApi(groupId: string): SGScopedGroup['decks'] {
  return {
    use: () =>
      useStudyGroupStore(
        useShallow((s) => Object.values(s.groups[groupId]?.deckProps ?? {})),
      ),
    deck: (deckId: string) => sgScopedDeck(groupId, deckId),
    add: (input) => {
      const uid = currentUserId();
      const deckId = uuid();
      const now = nowISO();
      // Build the SG row by spreading the caller's shared-scope payload
      // first, then overriding with SG-side identity + defaults. The
      // spread is schema-driven: any field on `SyncDeckMeta` that's
      // classified as `shared` in `DECK_FIELD_SCOPES` (e.g. `sources`)
      // flows through `sharing.share()` → `input.properties` → the SG
      // row automatically. Adding a new shared field doesn't require
      // touching this constructor — that's the whole point of the
      // unified entity registry.
      //
      // The SG-side identity (`id`, `deckName`, `addedByUserId`) and
      // defaults (`editors`, `editPermission`, `folderId`, `hideSource`,
      // timestamps) override anything from `input.properties` because
      // the SG row gets a fresh id and locked-down defaults regardless
      // of the source deck's state. Pre-Phase-C this constructor
      // enumerated every shared field by hand, which silently dropped
      // newly-added shared fields like `sources`.
      const props: SGDeckProps = {
        ...((input.properties ?? {}) as Partial<SGDeckProps>),
        id: deckId,
        deckName: input.deckName,
        addedByUserId: uid,
        // Default to locked-down: the deck owner is implicitly always
        // allowed; everyone else needs the owner to open it up via
        // "Manage access". Matches the server-side "missing
        // editPermission = owner-only" fallback.
        editors: [],
        editPermission: 'owner-only',
        folderId: input.folderId ?? null,
        hideSource: input.hideSource ?? false,
        sharedAtISO: now,
        updatedAtISO: now,
      };
      // Strip personal-scope fields BEFORE pushing to the group.
      // The owner's studyState / starred / actions / notes / tags /
      // chat must not be visible to other members. See
      // `stripCardForSG` for the full rationale.
      // Then stamp `updatedAtISO` (and `sharedUpdatedAtISO` to mirror
      // the per-scope timestamp invariant) so the server's LWW gate
      // accepts the rows.
      const stampedCards: SyncCardProps[] = input.cards.map((c) => {
        const clean = stripCardForSG(c);
        return {
          ...clean,
          sharedUpdatedAtISO: clean.sharedUpdatedAtISO ?? c.sharedUpdatedAtISO ?? now,
          updatedAtISO: c.updatedAtISO ?? now,
        };
      });
      useStudyGroupStore.getState().mergeDeckProps(groupId, [props]);
      useStudyGroupStore.getState().setDeckCards(groupId, deckId, stampedCards);
      // Phase F: route the share through the pending-mutations tracker
      // so callers can await durable persistence via `op-ack`. The
      // server's auto-emitted ack arrives only AFTER `await flushNow()`
      // returns inside `handleDeckPushMessage`, and Cloudflare's
      // output gate further holds the ack frame until the SQL commit
      // for both the deck meta + cards + (optionally) the inlined
      // notes Y.Doc completes. So "I shared a deck and refreshed
      // immediately" is now a strict guarantee, not a 5-second guess.
      //
      // The revert is the inverse of the optimistic local apply
      // above: drop the SG-side row + cards from the store. We
      // deliberately don't touch the personal deck's `linkedGroups`
      // — that link is added by `sharing.share()` via a separate
      // `deck-meta-push` on UserDo with its own settlement, so a
      // rejection on this leg should leave that one alone (the
      // personal deck-meta-push will simply find the SG side absent
      // on its server-side propagation pass and skip the fan-out).
      const mutationId = sendSGWithPreImage(
        groupId,
        {
          type: 'sg-deck-push',
          userId: uid,
          deckId,
          cards: stampedCards,
          properties: props,
          updatedAtISO: now,
          ...(input.notes && input.notes.length > 0 ? { notes: input.notes } : {}),
        },
        {
          scopes: ['shared'],
          revert: () => {
            useStudyGroupStore.getState().removeDeck(groupId, deckId);
          },
        },
      );
      return { deckId, mutationId };
    },
    move: (deckId, folderId) => {
      updateDeckProperty(groupId, deckId, { folderId });
    },
  };
}
